import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { api, formatErrorDetail } from "../lib/api";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .adminWhoami()
      .then((d) => {
        if (d.authenticated) navigate("/admin/listino", { replace: true });
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.adminLogin(password);
      navigate("/admin/listino", { replace: true });
    } catch (err) {
      setError(formatErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--bg-app)" }}
      >
        <div
          className="text-[13px] flex items-center gap-2"
          style={{ color: "var(--text-muted)" }}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Verifica accesso...
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--bg-app)" }}
      data-testid="admin-login"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl p-7 space-y-5 bg-white"
        style={{
          border: "1px solid var(--border-subtle)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div className="flex items-center gap-3">
          <Logo size={36} />
          <div className="leading-tight">
            <h1
              className="text-[17px] font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              Console admin
            </h1>
            <p
              className="text-[12px]"
              style={{ color: "var(--text-muted)" }}
            >
              Solo personale autorizzato
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="admin-password"
            className="block text-[12px] font-medium"
            style={{ color: "var(--text-secondary)" }}
          >
            Password
          </label>
          <div className="relative">
            <Lock
              className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-muted)" }}
            />
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-3 h-10 rounded-md outline-none text-[14px] transition-colors"
              style={{
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "var(--accent-primary)";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(37, 99, 235, 0.2)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "var(--border-default)";
                e.currentTarget.style.boxShadow = "none";
              }}
              data-testid="admin-password"
            />
          </div>
          {error && (
            <p
              className="text-[12px] rounded-md px-2.5 py-1.5"
              style={{
                background: "var(--semantic-red-bg)",
                color: "var(--semantic-red)",
                border: "1px solid #FECACA",
              }}
              data-testid="admin-login-error"
            >
              {error}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !password}
          className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md font-semibold text-[14px] transition-colors disabled:cursor-not-allowed"
          style={{
            background:
              loading || !password
                ? "var(--bg-subtle)"
                : "var(--accent-primary)",
            color:
              loading || !password
                ? "var(--text-muted)"
                : "var(--text-on-accent)",
            boxShadow: !loading && password ? "var(--shadow-sm)" : "none",
          }}
          onMouseEnter={(e) => {
            if (!loading && password)
              e.currentTarget.style.background = "var(--accent-hover)";
          }}
          onMouseLeave={(e) => {
            if (!loading && password)
              e.currentTarget.style.background = "var(--accent-primary)";
          }}
          data-testid="admin-login-submit"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Accesso in corso..." : "Entra"}
        </button>
      </form>
    </div>
  );
}
